import React from "react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
} from "../../components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "../../components/ui/field";
import { Input } from "../../components/ui/input";
import { useNavigate } from "react-router-dom";
import AuthenticationService from "../../sevices/auth.service";
import {
  toastError,
  toastSuccess,
} from "../../components/common/toastNotification";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Loader2, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = React.useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least 1 uppercase letter")
      .matches(/[0-9]/, "Password must contain at least 1 number")
      .matches(/[!@#$%^&*(),.?":{}|<>_\-+=]/, "Password must contain at least 1 special character")
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const response = await AuthenticationService.login({
          email: values.email,
          password: values.password,
        });
        console.log("Login response:", response);
        toastSuccess("Login Successfully");
        // Store the complete response which includes token and user data
        const authData = response?.data;
        localStorage.setItem("signova-admin", JSON.stringify(authData));
        formik.resetForm();
        navigate("/dashboard");
      } catch (error) {
        toastError(error?.response?.data?.message || "Something went wrong");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Ensure login fields are completely cleared whenever login page is mounted
  React.useEffect(() => {
    formik.resetForm({ values: { email: "", password: "" } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-4 bg-[#f1f3f6]">
      <div className="w-full max-w-[450px]">
        <Card className="border-none shadow-[0_4px_20px_0_rgba(0,0,0,0.05)] rounded-[20px] p-2 md:p-6 bg-white overflow-hidden">
          <CardHeader className="flex flex-col items-center pt-8 pb-10">
            <div className="mb-0">
              <img
                src="logo.png"
                alt="Signova Logo"
                className="h-40 object-contain"
              />
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Decoy hidden inputs to divert aggressive browser password managers */}
              <input
                type="text"
                name="prevent_autofill_email"
                id="prevent_autofill_email"
                style={{ display: "none" }}
                tabIndex={-1}
                autoComplete="off"
              />
              <input
                type="password"
                name="prevent_autofill_pwd"
                id="prevent_autofill_pwd"
                style={{ display: "none" }}
                tabIndex={-1}
                autoComplete="off"
              />

              <FieldGroup className="gap-4">
                <Field>
                  <FieldLabel
                    htmlFor="email"
                    className="text-[15px] font-semibold text-slate-700 mb-2"
                  >
                    Email
                  </FieldLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    className={`h-[56px] px-5 bg-[#f8faff] border-[#e2e8f0] focus:border-blue-400 transition-all rounded-xl text-slate-600 placeholder:text-slate-400 ${formik.touched.email && formik.errors.email
                      ? "border-red-400"
                      : ""
                      }`}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <FieldError className="text-sm text-red-500 font-medium">
                      {formik.errors.email}
                    </FieldError>
                  )}
                </Field>

                <Field>
                  <FieldLabel
                    htmlFor="password"
                    title="Password"
                    className="text-[15px] font-semibold text-slate-700 mb-2"
                  >
                    Password
                  </FieldLabel>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type="text"
                      style={{
                        WebkitTextSecurity: showPassword ? "none" : "disc",
                      }}
                      placeholder="Enter your password"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      spellCheck="false"
                      autoCorrect="off"
                      autoCapitalize="off"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      className={`h-[56px] px-5 pr-14 bg-[#f8faff] border-[#e2e8f0] focus:border-blue-400 transition-all rounded-xl text-slate-600 placeholder:text-slate-400 ${formik.touched.password && formik.errors.password
                        ? "border-red-400"
                        : ""
                        }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 stroke-[1.5]" />
                      ) : (
                        <Eye className="w-5 h-5 stroke-[1.5]" />
                      )}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <FieldError className="text-sm text-red-500 font-medium">
                      {formik.errors.password}
                    </FieldError>
                  )}
                </Field>

                <Button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="w-full h-[56px] text-base font-bold text-white bg-[#e8285e] hover:bg-[#c91f50] transition-colors rounded-xl mt-4 shadow-sm"
                >
                  {formik.isSubmitting ? (
                    <Loader2 className="animate-spin h-5 w-5" />
                  ) : (
                    "Sign in"
                  )}
                </Button>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
